import { useEffect, useRef, useState } from "react";

import axios from "axios";

import SHARED_ROUTES from "shared/config/apiRoutes";
import { API_URL } from "shared/lib/api/setupApi";
import { transformRoute } from "shared/utils/routing";
import { stripPrefix } from "shared/utils/vrn";

// Deduplicate concurrent in-flight requests per deal to avoid N+1 bursts.
// Intentionally not a long-lived cache to preserve data freshness.
const inFlightDealApplicationsRequests = new Map<string, Promise<any[]>>();

// Helper function to extract dealId from application VRN
const getDealIdFromApplicationVrn = (
  applicationVrn: string | null
): string | null => {
  if (!applicationVrn) return null;

  const stripped = stripPrefix(applicationVrn);
  const dealId = stripped.split("/").pop();
  return dealId || null;
};

const fetchDealApplicationsForDeal = async (dealId: string): Promise<any[]> => {
  const existingRequest = inFlightDealApplicationsRequests.get(dealId);
  if (existingRequest) {
    return existingRequest;
  }

  const url = `${API_URL}${transformRoute(SHARED_ROUTES.dealApplications, {
    id: dealId,
  })}`;

  const request = axios
    .get(url)
    .then((response) => response.data)
    .finally(() => {
      inFlightDealApplicationsRequests.delete(dealId);
    });

  inFlightDealApplicationsRequests.set(dealId, request);
  return request;
};

interface UseFetchMissingLegacyAppParams {
  applicant: {
    legacyId?: string | number;
    application?: string;
    isPrimaryApplication?: boolean;
  };
  legacyApp: any;
  refetchDeal?: () => void;
  shouldFetch?: boolean; // Optional flag to control when fetching should happen
}

interface UseFetchMissingLegacyAppReturn {
  // Returns a complete object ready for useAppRow
  legacyAppData: any;
  isFetching: boolean;
}

export const useFetchMissingLegacyApp = ({
  applicant,
  legacyApp,
  shouldFetch = true,
}: UseFetchMissingLegacyAppParams): UseFetchMissingLegacyAppReturn => {
  const [enhancedLegacyApp, setEnhancedLegacyApp] = useState(null);
  const [isFetching, setIsFetching] = useState(false);
  const hasAttemptedFetch = useRef(false);

  const {
    legacyId,
    application: applicationVrn,
    isPrimaryApplication,
  } = applicant || {};

  const { info: legacyInfo } = legacyApp || {};

  useEffect(() => {
    if (shouldFetch && !legacyInfo && legacyId && !hasAttemptedFetch.current) {
      hasAttemptedFetch.current = true;
      let isMounted = true;
      setIsFetching(true);

      const fetchApplicationData = async () => {
        try {
          // Extract dealId from applicant.application using VRN helper
          const dealId = getDealIdFromApplicationVrn(applicationVrn);

          if (!dealId) {
            return;
          }

          const applications = await fetchDealApplicationsForDeal(dealId);

          if (legacyId) {
            const targetApp = applications.find(
              (app: any) => app.id === Number(legacyId)
            );

            if (targetApp?.applicationSummary) {
              const { applicationSummary, unit, property } = targetApp;
              const {
                applicantVrn: summaryApplicantVrn,
                firstName,
                lastName,
                email,
                isSubmitted,
                applicationLink,
                canUpdateBlockDealSubmitIfIncomeDataIsMissing,
                canBeMoved,
                canBeRemoved,
                blockDealSubmitIfIncomeDataIsMissing,
              } = applicationSummary;
              // carry the real VERO submission timestamp so the
              // applicant pills/cards can tell a genuine submission apart from a
              // PMS (Yardi) auto-approval, which sets `isSubmitted` without one.
              const submittedAt =
                targetApp.submittedAt ?? applicationSummary.submittedAt;

              // Create enhanced legacyApp with info section and yardi data populated
              const enhancedLegacyAppData = {
                ...legacyApp, // Keep any existing data
                appId: targetApp.id,
                applicantVrn:
                  summaryApplicantVrn || `${dealId}:${targetApp.id}`,
                info: {
                  ...legacyInfo, // Keep any existing info
                  applicationId: targetApp.id,
                  name: `${firstName} ${lastName}`,
                  email,
                  isSubmitted,
                  submittedAt,
                  inviteLink: applicationLink,
                  permission: {
                    unblock:
                      canUpdateBlockDealSubmitIfIncomeDataIsMissing?.value,
                    move: canBeMoved?.value,
                    remove: canBeRemoved?.value,
                  },
                  isBlocked: blockDealSubmitIfIncomeDataIsMissing,
                },
                // Add unit and property data for Yardi extension
                unit,
                property,
              };

              if (isMounted) {
                setEnhancedLegacyApp(enhancedLegacyAppData);
              }
            }
          }
        } catch (error) {
        } finally {
          if (isMounted) {
            setIsFetching(false);
          }
        }
      };

      fetchApplicationData();

      return () => {
        isMounted = false;
        setIsFetching(false);
      };
    }

    return undefined;
  }, [legacyId, applicationVrn, legacyInfo, shouldFetch]);

  // Create complete legacyApp data with yardi props merged in
  const legacyAppToUse = enhancedLegacyApp || legacyApp;
  const { unit: legacyUnit, property: legacyProperty } = legacyAppToUse || {};

  const yardiProps =
    legacyUnit || legacyProperty
      ? {
          isPrimary: isPrimaryApplication || false,
          unit: legacyUnit,
          property: legacyProperty,
        }
      : undefined;

  // Return complete object with yardi props merged in
  const legacyAppData = {
    ...legacyAppToUse,
    yardi: yardiProps,
  };

  return {
    legacyAppData,
    isFetching,
  };
};
